import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, FileText, CalendarClock, Tag, Clock, Palette, Settings, LogOut, ExternalLink, Inbox, Sparkles } from "lucide-react";

const LOGO = "/brand/mtg_logo.svg";

const NAV = [
  { to: "/app", end: true, icon: LayoutDashboard, label: "Tableau de bord" },
  { to: "/app/nenzio", icon: Sparkles, label: "Nenzio" },
  { to: "/app/demandes", icon: FileText, label: "Demandes" },
  { to: "/app/reservations", icon: CalendarClock, label: "Réservations" },
  { to: "/app/prestations", icon: Tag, label: "Prestations & tarifs" },
  { to: "/app/disponibilites", icon: Clock, label: "Disponibilités" },
  { to: "/app/apparence", icon: Palette, label: "Apparence & intégration" },
  { to: "/app/parametres", icon: Settings, label: "Paramètres" },
];

export default function DashboardLayout() {
  const { user, tenant, logout } = useAuth();
  const navigate = useNavigate();
  const nav = user?.is_site_admin
    ? [...NAV, { to: "/app/prospects", icon: Inbox, label: "Prospects MonTempsGagné" }]
    : NAV;

  return (
    <div className="min-h-screen flex bg-[#F6F9FC]">
      <aside className="hidden md:flex md:w-64 flex-col border-r border-slate-200 bg-white">
        <div className="h-16 flex items-center px-6 border-b border-slate-100">
          <img src={LOGO} alt="MonTempsGagné" className="h-7" />
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-base ${
                  isActive ? "bg-[#EAF1F7] text-[#16324F]" : "text-slate-600 hover:bg-slate-50"
                }`
              }
              data-testid={`nav-${n.to.split("/").pop() || "overview"}`}
            >
              <n.icon className="w-4 h-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-100">
          <div className="px-3 py-2">
            <div className="text-xs text-slate-500">Connecté</div>
            <div className="text-sm font-semibold text-[#16324F] truncate">{user?.email}</div>
            <div className="text-xs text-slate-400 truncate">{tenant?.business_name}</div>
          </div>
          <a
            href={`/quote/${tenant?.slug}`}
            target="_blank" rel="noreferrer"
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-50"
            data-testid="preview-public"
          >
            <ExternalLink className="w-4 h-4" /> Voir le parcours public
          </a>
          <button
            onClick={async () => { await logout(); navigate("/"); }}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-50"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" /> Déconnexion
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0">
        <div className="md:hidden h-14 flex items-center justify-between px-4 border-b border-slate-200 bg-white">
          <img src={LOGO} alt="MonTempsGagné" className="h-6" />
          <button onClick={async () => { await logout(); navigate("/"); }} className="text-sm text-slate-500">Déconnexion</button>
        </div>
        <div className="md:hidden overflow-x-auto no-scrollbar flex gap-2 px-4 py-3 bg-white border-b border-slate-200 sticky top-0 z-10">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} className={({isActive}) => `whitespace-nowrap text-xs font-semibold px-3 py-1.5 rounded-full ${isActive ? "bg-[#16324F] text-white" : "bg-slate-100 text-slate-600"}`}>
              {n.label}
            </NavLink>
          ))}
        </div>
        <div className="p-6 md:p-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
